import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Tag, Switch, Button, Space, Typography, message, Modal, Form, Input, Row, Col, Statistic, Avatar, List, Tooltip, Popconfirm, Select } from 'antd';
import { PlusOutlined, TeamOutlined, SafetyOutlined, UserOutlined, EditOutlined, DeleteOutlined, KeyOutlined, CheckCircleOutlined, CloseCircleOutlined, LockOutlined } from '@ant-design/icons';
import { usersApi } from '../../../api/client';

const { Text } = Typography;

interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    lastLoginAt?: string;
    createdAt: string;
    roles: Role[];
}

interface Role {
    id: string;
    name: string;
    description?: string;
    isSystem: boolean;
    priority?: number;
    color?: string;
}

export function UsersTab() {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();

    const { data: usersData, isLoading } = useQuery({
        queryKey: ['users'],
        queryFn: async () => (await usersApi.getAll()).data,
    });

    const { data: rolesData } = useQuery({
        queryKey: ['roles'],
        queryFn: async () => (await usersApi.getRoles()).data,
    });

    const users: User[] = usersData || [];
    const roles: Role[] = rolesData || [];

    const createMutation = useMutation({
        mutationFn: (data: any) => usersApi.create(data),
        onSuccess: () => {
            message.success('Użytkownik utworzony');
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setIsModalOpen(false);
            form.resetFields();
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd tworzenia użytkownika');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => usersApi.update(id, data),
        onSuccess: () => {
            message.success('Użytkownik zaktualizowany');
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setIsModalOpen(false);
            setEditingUser(null);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => usersApi.delete(id),
        onSuccess: () => {
            message.success('Użytkownik usunięty');
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });

    const toggleMutation = useMutation({
        mutationFn: (user: User) => user.isActive ? usersApi.deactivate(user.id) : usersApi.activate(user.id),
        onSuccess: () => {
            message.success('Status zmieniony');
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });

    const resetPasswordMutation = useMutation({
        mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
            usersApi.resetPassword(id, newPassword),
        onSuccess: () => {
            message.success('Hasło zresetowane');
            setIsPasswordModalOpen(false);
            setSelectedUserId(null);
            passwordForm.resetFields();
        },
    });

    const handleOpenModal = (user?: User) => {
        if (user) {
            setEditingUser(user);
            form.setFieldsValue({
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                roleIds: user.roles.map(r => r.id),
            });
        } else {
            setEditingUser(null);
            form.resetFields();
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();
        if (editingUser) {
            updateMutation.mutate({ id: editingUser.id, data: values });
        } else {
            createMutation.mutate(values);
        }
    };

    const handleResetPassword = async () => {
        const values = await passwordForm.validateFields();
        if (selectedUserId) {
            resetPasswordMutation.mutate({ id: selectedUserId, newPassword: values.newPassword });
        }
    };

    const activeCount = users.filter(u => u.isActive).length;

    return (
        <div>
            {/* Stats */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="Wszyscy" value={users.length} prefix={<TeamOutlined />} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="Aktywni" value={activeCount} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="Nieaktywni" value={users.length - activeCount} valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="Role" value={roles.length} prefix={<SafetyOutlined />} />
                    </Card>
                </Col>
            </Row>

            {/* Users List */}
            <Card
                title={<Space><TeamOutlined /> Użytkownicy</Space>}
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>Dodaj</Button>}
            >
                <List
                    loading={isLoading}
                    dataSource={users}
                    renderItem={(user) => (
                        <List.Item
                            key={user.id}
                            actions={[
                                <Tooltip title="Edytuj" key="edit">
                                    <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(user)} />
                                </Tooltip>,
                                <Tooltip title="Resetuj hasło" key="reset">
                                    <Button type="text" icon={<KeyOutlined />} onClick={() => { setSelectedUserId(user.id); setIsPasswordModalOpen(true); }} />
                                </Tooltip>,
                                <Popconfirm key="delete" title="Usunąć użytkownika?" onConfirm={() => deleteMutation.mutate(user.id)} okText="Tak" cancelText="Nie">
                                    <Tooltip title="Usuń"><Button type="text" danger icon={<DeleteOutlined />} /></Tooltip>
                                </Popconfirm>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: user.isActive ? '#52c41a' : '#ff4d4f' }} />}
                                title={<Space>{user.firstName} {user.lastName} {!user.isActive && <Tag color="red">Nieaktywny</Tag>}</Space>}
                                description={
                                    <Space direction="vertical" size={0}>
                                        <Text type="secondary">{user.email}</Text>
                                        <Space size={4} style={{ marginTop: 4 }}>
                                            {user.roles.map(role => <Tag key={role.id} color={role.isSystem ? 'blue' : 'default'}>{role.name}</Tag>)}
                                        </Space>
                                    </Space>
                                }
                            />
                            <Switch
                                checked={user.isActive}
                                onChange={() => toggleMutation.mutate(user)}
                                checkedChildren="Aktywny"
                                unCheckedChildren="Nieaktywny"
                            />
                        </List.Item>
                    )}
                />
            </Card>

            {/* Create/Edit Modal */}
            <Modal
                title={editingUser ? 'Edytuj użytkownika' : 'Nowy użytkownik'}
                open={isModalOpen}
                onCancel={() => { setIsModalOpen(false); setEditingUser(null); form.resetFields(); }}
                onOk={handleSubmit}
                confirmLoading={createMutation.isPending || updateMutation.isPending}
                okText={editingUser ? 'Zapisz' : 'Utwórz'}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                        <Input prefix={<UserOutlined />} placeholder="email@firma.pl" />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="firstName" label="Imię" rules={[{ required: true }]}>
                                <Input placeholder="Jan" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastName" label="Nazwisko" rules={[{ required: true }]}>
                                <Input placeholder="Kowalski" />
                            </Form.Item>
                        </Col>
                    </Row>
                    {!editingUser && (
                        <Form.Item name="password" label="Hasło" rules={[{ required: true, min: 6 }]}>
                            <Input.Password prefix={<LockOutlined />} placeholder="Min. 6 znaków" />
                        </Form.Item>
                    )}
                    <Form.Item name="roleIds" label="Role">
                        <Select mode="multiple" placeholder="Wybierz role" options={roles.map(r => ({ value: r.id, label: r.name }))} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Reset Password Modal */}
            <Modal
                title="Resetuj hasło"
                open={isPasswordModalOpen}
                onCancel={() => { setIsPasswordModalOpen(false); setSelectedUserId(null); passwordForm.resetFields(); }}
                onOk={handleResetPassword}
                confirmLoading={resetPasswordMutation.isPending}
                okText="Resetuj"
            >
                <Form form={passwordForm} layout="vertical">
                    <Form.Item name="newPassword" label="Nowe hasło" rules={[{ required: true, min: 6 }]}>
                        <Input.Password prefix={<LockOutlined />} placeholder="Nowe hasło" />
                    </Form.Item>
                    <Form.Item name="confirmPassword" label="Potwierdź" dependencies={['newPassword']} rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                                return Promise.reject(new Error('Hasła się nie zgadzają'));
                            },
                        }),
                    ]}>
                        <Input.Password prefix={<LockOutlined />} placeholder="Potwierdź hasło" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
